import { DateTime } from 'luxon';

import type { IsoDateString } from 'types';

export const DATE_FORMAT = 'MM-dd-yyyy';

export function convertToISODateTimeString(dateStr: string, dateFormat = DATE_FORMAT) {
  const dt = DateTime.fromFormat(dateStr, dateFormat).toUTC();

  const isoDateString = dt.toISO() || undefined;

  return isoDateString;
}

export function convertToISODateString(dateStr: string, dateFormat = DATE_FORMAT): IsoDateString | undefined {
  const dt = DateTime.fromFormat(dateStr, dateFormat);

  return (dt.toISODate() as IsoDateString) || undefined;
}

export function formatDateToUsaShortStandard(dateString: string) {
  const date = DateTime.fromFormat(dateString, 'dd/MM/yyyy');
  if (!date.isValid) {
    return dateString;
  }

  return date.toFormat('MM/dd/yyyy');
}

/** Given 2 ISO date _strings_, returns the one that's earlier.
 * @param {IsoDateTimeString} oneDate
 * @param {IsoDateTimeString} theOtherDate
 */
export function getEarlierDateFromDateStrings(oneDate: string | undefined, theOtherDate: string | undefined) {
  if (!oneDate || !theOtherDate) {
    return oneDate ?? theOtherDate ?? '';
  }

  const date1 = DateTime.fromISO(oneDate).toMillis();
  const date2 = DateTime.fromISO(theOtherDate).toMillis();

  return date1 < date2 ? oneDate : theOtherDate;
}

export function safeISOToFormDate(isoString: string | undefined): string | undefined {
  if (!isoString) return undefined;
  const dt = DateTime.fromISO(isoString).toUTC();
  return dt.isValid ? dt.toFormat(DATE_FORMAT) : undefined;
}

export const formatDate = (date: Date | string | undefined, outputFormat = DateTime.DATE_MED) => {
  if (!date) return 'N/A';
  const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
  return dateTime.toLocaleString(outputFormat);
};

/** Concord's same-day processing cutoff, as an hour (0-23) in Arizona local time. */
export const CONCORD_SAME_DAY_CUTOFF_HOUR_AZ = 14; // 2 PM America/Phoenix (fixed UTC-7, no DST)

/**
 * Earliest payment date (YYYY-MM-DD) Concord will accept for a one-time payment: today in Arizona
 * before the 2 PM cutoff, otherwise the next day. The server re-validates this authoritatively;
 * we mirror it here so the customer submits the date their payment will actually land on.
 */
export function getConcordPaymentDate(now: DateTime = DateTime.utc()): string {
  const azNow = now.setZone('America/Phoenix');
  const cutoff = azNow.set({ hour: CONCORD_SAME_DAY_CUTOFF_HOUR_AZ, minute: 0, second: 0, millisecond: 0 });
  return (azNow >= cutoff ? azNow.plus({ days: 1 }) : azNow).toISODate() as string;
}
